import type { ItemDisplayColumn } from '@/api/hanly-v2/hanlyAPIV2.schemas';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { useShowPrices } from '@/contexts/auth';
import { OrderLinesView } from './order-lines-view';
import { OrderTotals } from './order-totals';
import type { OrderItemDisplayData, OrderTotalsData } from './types';

interface OrderConfirmModalProps {
  isOpen: boolean;
  isCreate?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  deliveryDateLabel: string;
  notes: string;
  items: OrderItemDisplayData[];
  itemDisplayColumns: readonly ItemDisplayColumn[];
  totals: OrderTotalsData;
}

export const OrderConfirmModal = ({
  isOpen,
  isCreate = false,
  onClose,
  onConfirm,
  isLoading,
  deliveryDateLabel,
  notes,
  items,
  itemDisplayColumns,
  totals,
}: OrderConfirmModalProps) => {
  const showPrices = useShowPrices();
  const lineCount = items.filter((item) => item.quantity > 0).length;
  return (
    <Sheet
      open={isOpen}
      onClose={isLoading ? () => undefined : onClose}
      title="ご注文内容の確認"
      className="md:max-w-4xl"
      footer={
        <div className="flex flex-col-reverse gap-2 md:flex-row md:justify-end">
          <Button variant="outlined" size="lg" onClick={onClose} disabled={isLoading} className="md:w-40">
            キャンセル
          </Button>
          <Button size="lg" onClick={onConfirm} loading={isLoading} className="md:w-56">
            {isCreate ? '注文する' : '注文を更新する'}
            {showPrices && `（${totals.totalAmount.toLocaleString()} 円）`}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex justify-between gap-3">
          <span className="text-neutral-600">希望納期</span>
          <span className="font-bold">{deliveryDateLabel}</span>
        </div>
        <section className="flex flex-col gap-2">
          <h4 className="font-bold">注文商品（{lineCount}品目）</h4>
          <OrderLinesView items={items} itemDisplayColumns={itemDisplayColumns} />
        </section>
        {notes && (
          <section className="flex flex-col gap-1">
            <h4 className="font-bold">備考</h4>
            <p className="text-sm whitespace-pre-wrap">{notes}</p>
          </section>
        )}
        {showPrices && (
          <div className="md:ml-auto md:w-96">
            <OrderTotals totals={totals} />
          </div>
        )}
      </div>
    </Sheet>
  );
};
